import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { fetchWithAbortRetry } from "../Utils/errors/supabaseAbortRetry";


export const SUPABASE_URL: string = "https://nneqehyphaavozzymtwp.supabase.co";
	// process.env.REACT_APP_ENV === "production"
	// 	? process.env.REACT_APP_SUPABASE_URL
	// 	: process.env.REACT_APP_SUPABASE_URL_PREVIEW;

/**
 * Browser API key.
 *
 * SECURITY FOLLOW-UP (do not swap blindly): the hardcoded value below is a
 * `service_role` JWT, not the project anon key. Replacing it with the real
 * anon key without auditing RLS will break public reads. Prefer
 * `REACT_APP_SUPABASE_ANON_KEY` when a verified anon key is available; rotate
 * the leaked service_role in the Supabase dashboard and move `auth.admin.*`
 * server-side.
 */
export const SUPABASE_ANON_KEY: string =
	(typeof process !== "undefined" &&
		process.env?.REACT_APP_SUPABASE_ANON_KEY?.trim()) ||
	"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5uZXFlaHlwaGFhdm96enltdHdwIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTY2Njg0MzAwNSwiZXhwIjoxOTgyNDE5MDA1fQ.z6uqaHkQZBjDSHCRf4JqmxlrkZ8KdPZmPtLzfKb3bXc";
	// process.env.REACT_APP_ENV === "production"
	// 	? process.env.REACT_APP_SUPABASE_ANON_KEY
	// 	: process.env.REACT_APP_SUPABASE_ANON_KEY_PREVIEW;

const SUPABASE_SERVICE_ROLE_KEY =
	(typeof process !== "undefined" &&
		process.env?.REACT_APP_SUPABASE_SERVICE_ROLE_KEY?.trim()) ||
	"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5uZXFlaHlwaGFhdm96enltdHdwIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTY2Njg0MzAwNSwiZXhwIjoxOTgyNDE5MDA1fQ.z6uqaHkQZBjDSHCRf4JqmxlrkZ8KdPZmPtLzfKb3bXc";

type SbGlobal = {
	__sb_supabase_v3?: SupabaseClient;
	__sb_supabase_public_v3?: SupabaseClient;
	__sb_supabase_admin_v3?: SupabaseClient;
};

const globalScope = globalThis as SbGlobal;

/** Shared fetch: auto-retry AbortError races on every page; skip intentional cancels. */
const clientOptionsBase = {
	global: {
		fetch: fetchWithAbortRetry,
	},
} as const;

/**
 * supabase-js 2.1.x wraps every REST call with `auth.getSession()` before fetch.
 * On public pages that races GoTrue (login / refresh / AuthProvider bootstrap)
 * and surfaces random AbortError even when we use a "session-free" client.
 *
 * Rebuild the PostgREST fetch so public reads use the API key directly and
 * never touch GoTrue. Bound at createClient time, so we must replace `fetch`
 * on both the client and its `rest` helper after construction.
 */
function buildSessionFreeFetch(apiKey: string) {
	return async (
		input: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> => {
		const headers = new Headers(init?.headers);
		if (!headers.has("apikey")) headers.set("apikey", apiKey);
		if (!headers.has("Authorization")) {
			headers.set("Authorization", `Bearer ${apiKey}`);
		}
		return fetchWithAbortRetry(input, { ...init, headers });
	};
}

function wireSessionFreeRestFetch(client: SupabaseClient, apiKey: string) {
	const fetch = buildSessionFreeFetch(apiKey);
	const anyClient = client as SupabaseClient & {
		fetch?: typeof fetch;
		rest?: { fetch?: typeof fetch };
	};
	anyClient.fetch = fetch;
	if (anyClient.rest) anyClient.rest.fetch = fetch;
}

/**
 * Primary browser auth client — singleton so HMR / re-imports don't spawn
 * extra GoTrueClient instances (which share the same storage lock and abort each other).
 */
export const supabase =
	globalScope.__sb_supabase_v3 ??=
	createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
		...clientOptionsBase,
		auth: {
			autoRefreshToken: true,
			persistSession: true,
			detectSessionInUrl: true,
		},
	});

/**
 * Session-free client for public website / LiB / follower reads.
 * Same API key as `supabase` (preserves current PostgREST access), but:
 * - no persisted session / auto-refresh / URL session detection
 * - distinct storageKey so it never contends with the auth client's GoTrue work
 * - REST fetch skips `getSession()` (see wireSessionFreeRestFetch)
 *
 * Public pages must use this client so bootstrap / search is not cancelled by
 * login / refresh races on the auth client.
 */
export const supabasePublic =
	globalScope.__sb_supabase_public_v3 ??=
	(() => {
		const client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
			...clientOptionsBase,
			auth: {
				autoRefreshToken: false,
				persistSession: false,
				detectSessionInUrl: false,
				storageKey: "sb-browsable-public-no-session",
			},
		});
		wireSessionFreeRestFetch(client, SUPABASE_ANON_KEY);
		return client;
	})();

/**
 * Admin API client (auth.admin.*). Must NOT share the default auth storage key
 * with `supabase`, or both GoTrueClients contend on the same navigator lock and
 * produce AbortError / "Connection was interrupted" on login and failed token refresh.
 */
export const supabaseAdmin =
	globalScope.__sb_supabase_admin_v3 ??=
	(() => {
		const client = createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY, {
			...clientOptionsBase,
			auth: {
				autoRefreshToken: false,
				persistSession: false,
				detectSessionInUrl: false,
				storageKey: "sb-browsable-admin-no-session",
			},
		});
		// Admin REST also has no browser session — skip getSession races.
		wireSessionFreeRestFetch(client, SUPABASE_SERVICE_ROLE_KEY);
		return client;
	})();


// for preview ---------------------

// import { createClient } from "@supabase/supabase-js";

// const supabaseUrl: any ='https://dqmncccmjrpgsdpvjbvp.supabase.co'
// 	// process.env.REACT_APP_ENV === "production"
// 	// 	? process.env.REACT_APP_SUPABASE_URL
// 	// 	: process.env.REACT_APP_SUPABASE_URL_PREVIEW;
// const supabaseAnonKey: any ='eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRxbW5jY2NtanJwZ3NkcHZqYnZwIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTY4NTU0OTczOCwiZXhwIjoyMDAxMTI1NzM4fQ.AhJXXNIdnO-T6BHCGPfkPaf3NjqXgeVp5FYeLOq-PiQ'
// 	// process.env.REACT_APP_ENV === "production"
// 	// 	? process.env.REACT_APP_SUPABASE_ANON_KEY
// 	// 	: process.env.REACT_APP_SUPABASE_ANON_KEY_PREVIEW;

// export const supabase = createClient(supabaseUrl, supabaseAnonKey);
